import React, {useEffect, useState} from 'react';
import {BaseInputProps} from '../..';

import IconButton from 'shared/migration-components/icon-button';
import {IoAdd, IoSearch, IoTrashBin} from 'react-icons/io5';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {Spotlight, spotlight} from '@mantine/spotlight';

import renderInputs from './render-inputs';
import DynamicSectionModal from './components/dynamic-section-modal';
import {NestedKeys} from 'shared/types/key-manipulation';
import {TranslationKeyProps} from 'shared/util/translated-props';
import {InputButtonProps} from '../input-button';
import {UseMutationResult} from 'react-query';
import {useDebounce} from 'use-debounce';
import Loading from 'shared/migration-components/loading';

import {
  Container,
  Row,
  Index,
  Title,
  Inputs,
  CenterdColumn,
  Header,
  Section,
} from './styles';
import getNestedKey from 'shared/util/get-nested-key';

export type GetSpotlightOptionsProps = (search?: string) => {
  data?: SpotlightOptions[];
  isLoading: boolean;
};

export interface SpotlightOptions {
  label: string;
  description?: string;
  value: any;
}

export interface DynamicSectionProps<T = any, D = T> extends BaseInputProps {
  name: NestedKeys<T>;
  inputs:
    | (() => BaseInputProps)[]
    | ((data: T, index: number) => (() => BaseInputProps)[]);
  onCreate?: {
    behavior: 'spotlight' | 'modal' | 'spotlight-modal';
    addManually?: boolean;
    inputs?: ((() => BaseInputProps) | NestedKeys<D>)[];
    disableWhenDefaultValue?: NestedKeys<D>[];
    title: string;
    spotlightOptions?: SpotlightOptions[] | GetSpotlightOptionsProps;
    addManuallyField?: string;
    mutation?: () => UseMutationResult<any, unknown, any, unknown>;
    validationSchema?: any;
    placeholder?: string;
  };
  editButton?: () => InputButtonProps<D>;
  index?: number;
  translationKey: TranslationKeyProps;
  detailsUrl?: string;
}

const DynamicSection: React.FC<DynamicSectionProps> = ({
  name,
  label,
  inputs,
  index,
  onCreate,
  translationKey,
  detailsUrl,
  editButton,
  border,
}) => {
  const {control} = useFormContext();
  const {fields, append, remove, update} = useFieldArray({
    control,
    name: name as string,
  });
  const [opened, setOpened] = useState(false);
  const [referenceValuesHistory, setReferenceValuesHistory] = useState<any>({});
  const [referenceValues, setReferenceValues] = useState<any>();
  const [defaultValues, setDefaultValues] = useState({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebounce(search, 1000);
  const {watch} = useFormContext();

  const {data, isLoading} =
    onCreate?.spotlightOptions &&
    typeof onCreate?.spotlightOptions === 'function'
      ? onCreate.spotlightOptions(debouncedSearch)
      : ({} as ReturnType<GetSpotlightOptionsProps>);

  useEffect(() => {
    if (!opened) {
      setDefaultValues({});
      setReferenceValues(undefined);
    }
  }, [opened]);

  const addManually = () => {
    if (!onCreate?.addManuallyField) return;
    setDefaultValues({[onCreate.addManuallyField]: search});
    closeSpotLight();
    setOpened(true);
  };

  const onSubmit = (data: any) => {
    const {index, ...rest} = data;
    const inputsSchema = (onCreate?.inputs ?? []).map((input) =>
      typeof input === 'string' ? input : input().name!,
    );

    const nextIndex = fields.length;

    if (data.index === undefined) {
      setReferenceValuesHistory({
        ...referenceValuesHistory,
        [nextIndex]: defaultValues,
      });
      append(data);
    } else {
      const oldData = getNestedKey(watch(), name)[index] as any;
      const dataToUpdate = Object.keys(rest).reduce((data, key) => {
        if (!inputsSchema.includes(key)) return data;

        return {...data, [key]: rest[key]};
      }, {});

      update(index, {...oldData, ...dataToUpdate});
    }

    closeSpotLight();
    setOpened(false);
  };

  const onSpotlightClick = (data: any) => {
    if (onCreate?.behavior === 'spotlight') onSubmit(data);
    setDefaultValues(data);
    setReferenceValues(data);
    setOpened(true);
    closeSpotLight();
  };

  const openSpotLight = () => {
    spotlight.open();
  };

  const closeSpotLight = () => {
    spotlight.close();
  };

  const getEditButton = (data: any, index: number) => {
    if (!editButton) return;

    const inputData = editButton();

    return renderInputs(
      {
        ...inputData,
        name: [name, index, inputData.name].join('.'),
        objectName: [name, index].join('.'),
        onClick: () => {
          setDefaultValues({...data, index});
          setReferenceValues(referenceValuesHistory[index]);
          setOpened(true);
        },
      } as InputButtonProps<any>,
      translationKey,
    );
  };

  const openModal = () => {
    closeSpotLight();
    setOpened(true);
  };

  return (
    <Container>
      <Row>
        {index && <Index>{index}</Index>}
        <Header>
          <Title>{label}</Title>
          {onCreate?.behavior === 'spotlight-modal' && onCreate.addManually && (
            <Section onClick={openModal}>
              <IoAdd />
              Passageiro externo
            </Section>
          )}
        </Header>
        <IconButton
          icon={<IoAdd />}
          label="Adicionar"
          variant="filled"
          onClick={() =>
            !onCreate
              ? append({})
              : onCreate.behavior === 'modal'
              ? setOpened(true)
              : openSpotLight()
          }
        />
      </Row>
      {fields.map((field, index) => (
        <Inputs key={index}>
          {getEditButton(field, index)}
          {typeof inputs === 'function'
            ? inputs(watch(), index).map((getInput) => {
                const input = getInput();

                return renderInputs(
                  {
                    ...input,
                    name: [name, index, input.name].join('.'),
                    border: input.border ?? border,
                  },
                  translationKey,
                );
              })
            : inputs.map((getInput) => {
                const input = getInput();

                return renderInputs(
                  {
                    ...input,
                    name: [name, index, input.name].join('.'),
                    border: input.border ?? border,
                  },
                  translationKey,
                );
              })}
          <IconButton
            label="Remover"
            icon={<IoTrashBin />}
            onClick={() => remove(index)}
          />
        </Inputs>
      ))}
      {onCreate && onCreate.behavior !== 'modal' && (
        <Spotlight
          actions={(typeof onCreate.spotlightOptions !== 'function'
            ? onCreate.spotlightOptions
            : data ?? [])!.map((option, index) => ({
            ...option,
            id: String(index),
            onClick: () => onSpotlightClick(option.value),
          }))}
          limit={8}
          onQueryChange={setSearch}
          query={search}
          {...{
            filter:
              typeof onCreate?.spotlightOptions === 'function'
                ? (_, actions) => actions
                : undefined,
          }}
          searchProps={{
            leftSection: <IoSearch />,
            placeholder: onCreate.placeholder ?? 'Pesquisar...',
          }}
          nothingFound={
            search && typeof inputs === 'function' ? (
              <>
                {(isLoading || !data) && (
                  <CenterdColumn>
                    <Loading />
                  </CenterdColumn>
                )}
                {!isLoading && data?.length === 0 && (
                  <CenterdColumn>
                    Nenhum registro encontrado...
                    {onCreate.addManuallyField && (
                      <IconButton
                        label="Criar novo"
                        icon={<IoAdd />}
                        variant="filled"
                        onClick={addManually}
                      />
                    )}
                  </CenterdColumn>
                )}
              </>
            ) : (
              <CenterdColumn>Nenhum registro encontrado...</CenterdColumn>
            )
          }
        />
      )}
      {onCreate &&
        opened &&
        onCreate.behavior !== 'spotlight' &&
        onCreate.inputs?.length && (
          <DynamicSectionModal
            {...{
              opened,
              onClose: () => setOpened(false),
              inputs: onCreate.inputs as any,
              onSubmit,
              title: onCreate.title,
              defaultValues,
              translationKey,
              detailsUrl,
              mutation: 'id' in defaultValues ? undefined : onCreate.mutation,
              validationSchema: onCreate.validationSchema,
              referenceValues,
              disableWhenDefaultValue: onCreate.disableWhenDefaultValue,
            }}
          />
        )}
    </Container>
  );
};

export default DynamicSection;
